import { motion } from "framer-motion";
import { ArrowUpRight, CalendarDays, CheckCircle2, Clock, Lock, MapPin, Users } from "lucide-react";

const fmtDate = (iso) =>
  new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso));

const fmtTime = (iso) =>
  new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(new Date(iso));

const cardVariants = {
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function TournamentCard({ tournament: t, index, onRegister }) {
  const isFull = t.registeredTeams >= t.capacity;
  const isConfirmed = t.status === "confirmed";
  const disabled = isFull || isConfirmed;
  const pct = Math.min(100, Math.round((t.registeredTeams / t.capacity) * 100));

  return (
    <motion.article
      variants={cardVariants}
      whileHover={disabled ? undefined : { y: -8 }}
      data-testid={`tournament-card-container-${index}`}
      className="glass-card group relative flex flex-col overflow-hidden rounded-3xl p-6 transition-colors duration-300 hover:border-yellow-400/40 sm:p-7"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-purple-600/20 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />

      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <p className="font-mono-b3b text-[10px] uppercase tracking-[0.3em] text-purple-300">
            Bracket {String(index + 1).padStart(2, "0")}
          </p>
          <h3
            data-testid={`tournament-date-${index}`}
            className="mt-1 font-display text-3xl uppercase leading-none tracking-wide text-white sm:text-4xl"
          >
            {fmtDate(t.start)}
          </h3>
        </div>
        {isConfirmed ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-[#1E1B4B] px-2.5 py-1 font-mono-b3b text-[10px] uppercase tracking-widest text-[#C084FC]">
            <CheckCircle2 className="h-3 w-3" /> Confirmed
          </span>
        ) : isFull ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-[#7F1D1D] px-2.5 py-1 font-mono-b3b text-[10px] uppercase tracking-widest text-[#FCA5A5]">
            <Lock className="h-3 w-3" /> Full
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md bg-[#14532D] px-2.5 py-1 font-mono-b3b text-[10px] uppercase tracking-widest text-[#4ADE80]">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-[#4ADE80]" /> Open
          </span>
        )}
      </div>

      <div className="space-y-2.5 text-sm text-slate-300">
        <p data-testid={`tournament-time-${index}`} className="flex items-center gap-2.5">
          <Clock className="h-4 w-4 shrink-0 text-yellow-400" />
          {fmtTime(t.start)} – {fmtTime(t.end)}
        </p>
        <p data-testid={`tournament-location-${index}`} className="flex items-center gap-2.5">
          <MapPin className="h-4 w-4 shrink-0 text-yellow-400" />
          {t.location || "Venue TBA"}
        </p>
        <p data-testid={`tournament-registered-count-${index}`} className="flex items-center gap-2.5">
          <Users className="h-4 w-4 shrink-0 text-yellow-400" />
          {t.registeredTeams} of {t.capacity} teams
        </p>
      </div>

      <div className="mt-5">
        <div className="h-2 overflow-hidden rounded-full bg-purple-950/80">
          <motion.div
            data-testid={`tournament-progress-bar-${index}`}
            initial={{ width: 0 }}
            whileInView={{ width: `${pct}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className={`h-full rounded-full ${isFull ? "bg-red-400" : "bg-gradient-to-r from-purple-500 to-yellow-400"}`}
          />
        </div>
      </div>

      <button
        data-testid={`tournament-register-button-${index}`}
        disabled={disabled}
        onClick={onRegister}
        className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-heading text-sm font-bold uppercase tracking-widest transition-all ${
          disabled
            ? "cursor-not-allowed bg-purple-950/60 text-slate-500"
            : "bg-yellow-400 text-black shadow-[0_0_24px_rgba(250,204,21,0.25)] hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(250,204,21,0.45)] active:scale-95"
        }`}
      >
        {isConfirmed ? (
          "Tournament confirmed"
        ) : isFull ? (
          "Bracket full"
        ) : (
          <>
            Register your team <ArrowUpRight className="h-4 w-4" />
          </>
        )}
      </button>
    </motion.article>
  );
}
